import { gql } from "@apollo/client";
import { updateCustomer } from "components/Signup/store/slices/customer";
import { useDispatch } from "react-redux";
import { useWebTranslation } from "translations";
import ChatMessage, { ChatContainer } from "ui-kit/ChatMessage/ChatMessage";
import Gutter from "ui-kit/Gutter";
import { Typography } from "ui-kit/Typography";
import { formatErrorMessage } from "utils/validation";
import client from "../../../../../apollo-client";
import {
  SearchPostcodes,
  SearchPostcodesVariables,
} from "../../../../../__generated__/SearchPostcodes";
import { StepProps } from "../types";
import FlowButtons from "components/Signup/components/FlowButtons/FlowButtons";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
import { useCustomer } from "components/Signup/store/selectors/customer";
import { useError } from "components/Signup/store/selectors/metadata";
import { SelectBox } from "ui-kit";
import { useCallback, useMemo, useState } from "react";
import debounce from "lodash.debounce";

type PostCodeOption = {
  id: string;
  name: string;
  city: string;
  zip: string;
};

const SEARCH_DEBOUNCE_TIMEOUT = 300;
const QUERY_MIN_LENGTH = 3;

const UserZip = ({ previous, next, isValid, transitioning }: StepProps) => {
  const { t } = useWebTranslation();
  const [items, setItems] = useState<PostCodeOption[]>([]);

  const dispatch = useDispatch();
  const { region, address } = useCustomer();

  const error = useError();

  const fetchPostcodes = useMemo(
    () =>
      debounce(async (input: string) => {
        const { data } = await client.query<
          SearchPostcodes,
          SearchPostcodesVariables
        >({
          query: SEARCH_POSTCODES,
          variables: {
            input,
          },
        });

        const results =
          data.searchPostcodes?.results.map<PostCodeOption>((result) => ({
            id: result.value,
            name: `${result.code} ${result.text}`,
            city: result.text,
            zip: result.code,
          })) ?? [];

        setItems(results);
      }, SEARCH_DEBOUNCE_TIMEOUT),
    []
  );

  const onInputChange = useCallback(
    ({ inputValue }: { inputValue: string }) => {
      if (inputValue.length >= QUERY_MIN_LENGTH) {
        fetchPostcodes(inputValue);
      }
    },
    [fetchPostcodes]
  );

  return (
    <ChatContainer leaving={transitioning}>
      <ChatMessage>
        <Typography>{t("signup.user_zip.text")}</Typography>
        <Gutter size={1.5} />
        <Typography color="grey400">
          {t("signup.user_zip.description")}
        </Typography>
      </ChatMessage>
      <Gutter size={2} />
      <ChatMessage fromMe small>
        <SelectBox<PostCodeOption>
          onInputValueChange={onInputChange}
          onSelectedItemChange={({ selectedItem }) => {
            const { zip = null, id = null, city = null } = selectedItem || {};

            dispatch(
              updateCustomer({
                address: {
                  city,
                  zip,
                  line1: address?.line1 ?? null,
                },
                region: id,
              })
            );
          }}
          items={items}
          itemToString={(item) => item?.name ?? ""}
          defaultSelectedItem={
            region
              ? {
                  id: region,
                  name: `${address?.zip ?? ""} ${address?.city ?? ""}`,
                  city: address?.city ?? "",
                  zip: address?.zip ?? "",
                }
              : undefined
          }
          placeholder={t("signup.user_zip.region.placeholder")}
        />
      </ChatMessage>
      {!!error && (
        <ErrorMessage>
          <Typography color="error">{formatErrorMessage(error)}</Typography>
        </ErrorMessage>
      )}
      <FlowButtons
        inProgress={transitioning}
        next={next}
        disabled={!isValid}
        previous={previous}
        backButton={false}
      />
    </ChatContainer>
  );
};

const SEARCH_POSTCODES = gql`
  query SearchPostcodes($input: String!) {
    searchPostcodes(input: $input) {
      results {
        value
        text
        code
      }
    }
  }
`;

export default UserZip;
